import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { RowSelectionState, OnChangeFn } from '@tanstack/react-table';
import { Button, Tooltip } from '@heroui/react';
import { DataTable, type ColumnMetaExt } from '@/components/common/DataTable';
import { QueueStatusChip, QueuePriorityChip } from '@/features/queue/components/QueueChips';
import { formatBytes, formatRelative } from '@/lib/format';
import type { QueueMessage, QueueMessageActionType } from '@/features/queue/types';
import { ArrowPathIcon, BoltIcon, ArrowsRightLeftIcon, TrashIcon } from '@/icons';

/** The Delivery Center message table: selectable rows, per-row actions, row → drawer. */
export function QueueMessagesTable({
  messages, isLoading, isError, onRetry, sort, onSort, onView,
  selection, onSelectionChange, onRowAction,
}: {
  messages: QueueMessage[]; isLoading: boolean; isError?: boolean; onRetry?: () => void;
  sort: string; onSort: (f: string) => void; onView: (m: QueueMessage) => void;
  selection: RowSelectionState; onSelectionChange: OnChangeFn<RowSelectionState>;
  onRowAction: (action: QueueMessageActionType, m: QueueMessage) => void;
}) {
  const columns = useMemo<ColumnDef<QueueMessage, unknown>[]>(() => [
    { id: 'queueId', header: 'Queue ID', meta: { className: 'font-mono text-xs' } as ColumnMetaExt, cell: ({ row }) => row.original.queueId },
    { id: 'who', header: 'Sender → recipient', cell: ({ row }) => (
      <div className="min-w-0">
        <p className="truncate font-medium text-fg">{row.original.sender}</p>
        <p className="truncate text-xs text-fg-muted">→ {row.original.recipient}</p>
      </div>
    ) },
    { id: 'subject', header: 'Subject', cell: ({ row }) => (
      <Tooltip content={row.original.subject || '(no subject)'}>
        <p className="max-w-[14rem] truncate text-sm text-fg">{row.original.subject || '(no subject)'}</p>
      </Tooltip>
    ) },
    { id: 'status', header: 'Status', cell: ({ row }) => <QueueStatusChip value={row.original.status} /> },
    { id: 'retries', header: 'Retry', meta: { sortKey: 'retries', className: 'text-center font-mono' } as ColumnMetaExt, cell: ({ row }) => row.original.retries },
    { id: 'priority', header: 'Priority', cell: ({ row }) => <QueuePriorityChip value={row.original.priority} /> },
    { id: 'tenant', header: 'Tenant', cell: ({ row }) => <span className="whitespace-nowrap text-sm text-fg-muted">{row.original.tenant}</span> },
    { id: 'age', header: 'Age', meta: { sortKey: 'arrivalTime' } as ColumnMetaExt, cell: ({ row }) => <span className="whitespace-nowrap text-sm text-fg-muted">{formatRelative(row.original.arrivalTime)}</span> },
    { id: 'next', header: 'Next retry', cell: ({ row }) => <span className="whitespace-nowrap text-sm text-fg-muted">{row.original.nextRetry ? formatRelative(row.original.nextRetry) : '—'}</span> },
    { id: 'worker', header: 'Worker', meta: { className: 'font-mono text-xs' } as ColumnMetaExt, cell: ({ row }) => <span className="text-fg-muted">{row.original.worker ?? '—'}</span> },
    { id: 'size', header: 'Size', cell: ({ row }) => <span className="whitespace-nowrap text-sm text-fg-muted">{formatBytes(row.original.sizeBytes)}</span> },
    { id: 'actions', header: '', meta: { className: 'text-right' } as ColumnMetaExt, cell: ({ row }) => (
      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        <Tooltip content="Retry"><Button isIconOnly size="sm" variant="light" aria-label="Retry" onPress={() => onRowAction('retry', row.original)}><ArrowPathIcon className="h-4 w-4" /></Button></Tooltip>
        <Tooltip content="Force delivery"><Button isIconOnly size="sm" variant="light" aria-label="Force delivery" onPress={() => onRowAction('force', row.original)}><BoltIcon className="h-4 w-4" /></Button></Tooltip>
        <Tooltip content="Move queue"><Button isIconOnly size="sm" variant="light" aria-label="Move queue" onPress={() => onRowAction('move', row.original)}><ArrowsRightLeftIcon className="h-4 w-4" /></Button></Tooltip>
        <Tooltip content="Delete"><Button isIconOnly size="sm" variant="light" color="danger" aria-label="Delete" onPress={() => onRowAction('delete', row.original)}><TrashIcon className="h-4 w-4" /></Button></Tooltip>
      </div>
    ) },
  ], [onRowAction]);

  return (
    <DataTable<QueueMessage>
      columns={columns} data={messages} getRowId={(m) => m.queueId}
      isLoading={isLoading} isError={isError} onRetry={onRetry}
      enableSelection rowSelection={selection} onRowSelectionChange={onSelectionChange}
      sort={sort} onSort={onSort} onRowClick={onView}
      emptyMessage="No messages in this queue match your filters."
    />
  );
}
